import { createTheme, alpha, Theme } from '@mui/material/styles';
import { brand, slate, status, fonts, radius, shadowScale } from './tokens';

export * from './tokens';

declare module '@mui/material/styles' {
  interface TypeBackground {
    /** Recessed surface: table heads, inset panels, code blocks. */
    subtle: string;
    /** Raised surface above `paper`: menus, popovers, hover cards. */
    elevated: string;
  }
}

/**
 * Read the palette through CSS variables when they exist so switching colour
 * scheme repaints without a React re-render. Falls back to the static palette.
 */
const pal = (theme: Theme) => ((theme as any).vars ?? theme).palette;

const at = (obj: any, path: string) =>
  path.split('.').reduce((value, key) => (value == null ? value : value[key]), obj);

/**
 * Translucent tint of a palette colour, e.g. `tint(theme, 'warning.main', 0.14)`.
 *
 * Do not reach for `alpha()` here. With CSS variables on, `theme.vars.palette.*`
 * is the string `var(--…)`, which `alpha()` throws on; and `theme.palette.*`
 * holds only the default scheme's value, so a tint built from it would stay
 * light in dark mode. MUI emits a `*Channel` token ("217 119 6") per palette
 * colour that works in both schemes — use that.
 */
export const tint = (theme: Theme, path: string, opacity: number): string => {
  const dot = path.lastIndexOf('.');
  const channelPath =
    dot === -1 ? `${path}Channel` : `${path.slice(0, dot)}.${path.slice(dot + 1)}Channel`;

  const channel = at(pal(theme), channelPath);
  if (channel) return `rgba(${channel} / ${opacity})`;

  // Custom palette entries (background.subtle/elevated) have no channel token.
  return alpha(at(theme.palette, path), opacity);
};

/** MUI wants exactly 25 elevation strings; our scale has 6 meaningful steps. */
const buildShadows = () => {
  const scale = shadowScale('light');
  return Array.from({ length: 25 }, (_, i) => scale[Math.min(i, scale.length - 1)]) as any;
};

const theme = createTheme({
  cssVariables: { colorSchemeSelector: 'class', cssVarPrefix: 'agentx' },
  defaultColorScheme: 'light',
  colorSchemes: {
    light: {
      palette: {
        mode: 'light',
        primary: { main: brand[500], light: brand[400], dark: brand[600], contrastText: '#FFFFFF' },
        secondary: { main: brand[400], light: brand[300], dark: brand[600], contrastText: '#FFFFFF' },
        success: { main: status.success.main, dark: status.success.dark, contrastText: '#FFFFFF' },
        warning: { main: status.warning.main, dark: status.warning.dark, contrastText: '#FFFFFF' },
        error: { main: status.error.main, dark: status.error.dark, contrastText: '#FFFFFF' },
        info: { main: status.info.main, dark: status.info.dark, contrastText: '#FFFFFF' },
        background: {
          default: slate[50],
          paper: '#FFFFFF',
          subtle: slate[100],
          elevated: '#FFFFFF',
        },
        text: { primary: slate[900], secondary: slate[500], disabled: slate[400] },
        divider: slate[200],
        grey: {
          50: slate[50],
          100: slate[100],
          200: slate[200],
          300: slate[300],
          400: slate[400],
          500: slate[500],
          600: slate[600],
          700: slate[700],
          800: slate[800],
          900: slate[900],
        },
        action: {
          hover: alpha(brand[500], 0.05),
          selected: alpha(brand[500], 0.1),
          focus: alpha(brand[500], 0.16),
          disabledOpacity: 0.42,
        },
      },
    },
    dark: {
      palette: {
        mode: 'dark',
        // Dark mode lifts the hue and darkens the text on top of it rather than
        // inverting light values, which keeps links legible at ~7:1 on canvas.
        primary: { main: brand[400], light: '#A08ACE', dark: brand[500], contrastText: '#FFFFFF' },
        secondary: { main: '#A08ACE', light: '#B5A5DC', dark: brand[600], contrastText: '#FFFFFF' },
        success: { main: status.success.onDark, dark: status.success.main, contrastText: '#04231A' },
        warning: { main: status.warning.onDark, dark: status.warning.main, contrastText: '#2A1A02' },
        error: { main: status.error.onDark, dark: status.error.main, contrastText: '#2B0A0A' },
        info: { main: status.info.onDark, dark: status.info.main, contrastText: '#04202E' },
        background: {
          default: slate[900],
          paper: '#252526',
          subtle: '#181818',
          elevated: '#313131',
        },
        text: { primary: '#CCCCCC', secondary: '#9D9D9D', disabled: '#858585' },
        divider: '#2B2B2B',
        action: {
          hover: 'rgba(255,255,255,0.06)',
          selected: 'rgba(139,111,192,0.14)',
          focus: 'rgba(139,111,192,0.24)',
          disabledOpacity: 0.42,
        },
      },
    },
  },

  shape: { borderRadius: radius.md },
  shadows: buildShadows(),
  breakpoints: { values: { xs: 0, sm: 640, md: 960, lg: 1280, xl: 1600 } },

  typography: {
    fontFamily: fonts.sans,
    htmlFontSize: 16,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: { fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.2 },
    h2: { fontSize: '1.875rem', fontWeight: 700, letterSpacing: '-0.022em', lineHeight: 1.25 },
    h3: { fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.3 },
    h4: { fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.35 },
    h5: { fontSize: '1.0625rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.4 },
    h6: { fontSize: '0.9375rem', fontWeight: 600, letterSpacing: '-0.005em', lineHeight: 1.45 },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.6 },
    caption: { fontSize: '0.75rem', lineHeight: 1.5 },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 700,
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      lineHeight: 1.4,
    },
    button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: (themeParam) => ({
        '*, *::before, *::after': { boxSizing: 'border-box' },
        html: { WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' },
        body: {
          backgroundColor: pal(themeParam).background.default,
          color: pal(themeParam).text.primary,
        },
        // Figures only line up in columns with tabular numerals.
        '.tnum, table td, table th': { fontVariantNumeric: 'tabular-nums' },
        '.mono': { fontFamily: fonts.mono, fontSize: '0.8125rem', letterSpacing: '-0.01em' },
        '::selection': { background: alpha(brand[500], 0.22) },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          borderRadius: 8,
          border: '3px solid transparent',
          backgroundClip: 'content-box',
          backgroundColor: alpha(slate[400], 0.45),
        },
        '*::-webkit-scrollbar-thumb:hover': { backgroundColor: alpha(slate[400], 0.7) },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
          },
        },
      }),
    },

    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
      styleOverrides: {
        root: ({ theme: t }) => ({
          backgroundColor: tint(t, 'background.paper', 0.78),
          backdropFilter: 'blur(12px)',
          color: pal(t).text.primary,
          borderBottom: '1px solid ' + pal(t).divider,
          boxShadow: 'none',
        }),
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme: t }) => ({
          backgroundImage: 'none',
          backgroundColor: pal(t).background.paper,
          borderRight: '1px solid ' + pal(t).divider,
        }),
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: ({ theme: t }) => ({ borderColor: pal(t).divider }),
        rounded: { borderRadius: radius.lg },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: radius.lg,
          border: '1px solid ' + pal(t).divider,
          backgroundColor: pal(t).background.paper,
          boxShadow: 'none',
        }),
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { padding: '18px 20px 0' },
        title: { fontSize: '0.9375rem', fontWeight: 600 },
        subheader: { fontSize: '0.8125rem' },
      },
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: 20, '&:last-child': { paddingBottom: 20 } } },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          paddingInline: 16,
          minHeight: 40,
          transition: 'background-color 160ms ease, border-color 160ms ease, color 160ms ease',
        },
        sizeSmall: { minHeight: 32, paddingInline: 12, fontSize: '0.8125rem' },
        sizeLarge: { minHeight: 46, paddingInline: 22, fontSize: '0.9375rem' },
        outlined: ({ theme: t }) => ({ borderColor: pal(t).divider }),
        text: { paddingInline: 10 },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: radius.sm, transition: 'background-color 160ms ease' },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: radius.md,
          backgroundColor: pal(t).background.paper,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: pal(t).divider },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: pal(t).text.disabled },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
        }),
        input: { fontSize: '0.875rem' },
        inputSizeSmall: { fontSize: '0.8125rem' },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { fontSize: '0.875rem' } } },
    MuiFormHelperText: { styleOverrides: { root: { marginLeft: 2, fontSize: '0.75rem' } } },
    MuiTextField: { defaultProps: { size: 'small', variant: 'outlined' } },
    MuiSelect: { defaultProps: { size: 'small' } },

    MuiTableContainer: { styleOverrides: { root: { overflowX: 'auto' } } },
    MuiTable: { styleOverrides: { root: { borderCollapse: 'separate', borderSpacing: 0 } } },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          backgroundColor: pal(t).background.subtle,
          '& .MuiTableCell-head': {
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: pal(t).text.secondary,
            whiteSpace: 'nowrap',
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderBottom: '1px solid ' + pal(t).divider,
          padding: '12px 16px',
          fontSize: '0.8125rem',
        }),
        sizeSmall: { padding: '8px 12px' },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          transition: 'background-color 140ms ease',
          '&:last-child .MuiTableCell-body': { borderBottom: 0 },
          '&.MuiTableRow-hover:hover': { backgroundColor: pal(t).action.hover },
        }),
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: ({ theme: t }) => ({ borderTop: '1px solid ' + pal(t).divider }),
        selectLabel: { fontSize: '0.8125rem' },
        displayedRows: { fontSize: '0.8125rem' },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radius.sm, fontWeight: 600 },
        sizeSmall: { height: 22, fontSize: '0.6875rem' },
        label: { paddingInline: 8 },
        outlined: ({ theme: t }) => ({ borderColor: pal(t).divider }),
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: ({ theme: t }) => ({
          borderRadius: radius.xl,
          border: '1px solid ' + pal(t).divider,
          backgroundImage: 'none',
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontSize: '1.0625rem', fontWeight: 600, padding: '20px 24px 8px' } },
    },
    MuiDialogContent: { styleOverrides: { root: { padding: '8px 24px' } } },
    MuiDialogActions: { styleOverrides: { root: { padding: '16px 24px 20px', gap: 8 } } },
    MuiBackdrop: {
      styleOverrides: {
        root: { backgroundColor: 'rgba(8,13,24,0.55)', backdropFilter: 'blur(2px)' },
        invisible: { backgroundColor: 'transparent', backdropFilter: 'none' },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: ({ theme: t }) => ({
          borderRadius: radius.lg,
          border: '1px solid ' + pal(t).divider,
          backgroundColor: pal(t).background.elevated,
          boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
          ...t.applyStyles('dark', { boxShadow: '0 4px 14px rgba(0,0,0,0.32)' }),
        }),
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: { root: { borderRadius: radius.sm, fontSize: '0.875rem', minHeight: 38 } },
    },
    MuiPopover: {
      styleOverrides: {
        paper: ({ theme: t }) => ({
          borderRadius: radius.lg,
          border: '1px solid ' + pal(t).divider,
          backgroundColor: pal(t).background.elevated,
        }),
      },
    },
    MuiListItemButton: { styleOverrides: { root: { borderRadius: radius.md } } },
    MuiListItemIcon: { styleOverrides: { root: { minWidth: 36, color: 'inherit' } } },

    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          borderRadius: radius.sm,
          fontSize: '0.75rem',
          padding: '6px 10px',
          backgroundColor: slate[800],
        },
        arrow: { color: slate[800] },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: radius.md, fontSize: '0.8125rem', alignItems: 'center' },
        standardError: ({ theme: t }) => ({ border: '1px solid ' + tint(t, 'error.main', 0.28) }),
        standardSuccess: ({ theme: t }) => ({ border: '1px solid ' + tint(t, 'success.main', 0.28) }),
        standardWarning: ({ theme: t }) => ({ border: '1px solid ' + tint(t, 'warning.main', 0.28) }),
        standardInfo: ({ theme: t }) => ({ border: '1px solid ' + tint(t, 'info.main', 0.28) }),
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: ({ theme: t }) => ({ minHeight: 42, borderBottom: '1px solid ' + pal(t).divider }),
        indicator: { height: 2, borderRadius: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { minHeight: 42, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' },
      },
    },

    MuiLinearProgress: { styleOverrides: { root: { borderRadius: radius.pill, height: 6 } } },
    MuiAvatar: { styleOverrides: { root: { fontSize: '0.8125rem', fontWeight: 600 } } },
    MuiSwitch: { defaultProps: { size: 'small' } },
    MuiCheckbox: { defaultProps: { size: 'small' } },
    MuiRadio: { defaultProps: { size: 'small' } },
    MuiLink: { defaultProps: { underline: 'hover' }, styleOverrides: { root: { fontWeight: 500 } } },
    MuiDivider: { styleOverrides: { root: ({ theme: t }) => ({ borderColor: pal(t).divider }) } },
    MuiSkeleton: { styleOverrides: { root: { borderRadius: radius.sm } } },
    MuiStepLabel: { styleOverrides: { label: { fontSize: '0.8125rem', fontWeight: 600 } } },
  },
});

export default theme;
